import { addNumberSeparator } from '@va/util/helpers';
import { ChartData, CoreScaleOptions, Scale } from 'chart.js';

export type MinMaxValues = { minValue: number; maxValue: number };

const minMaxByData = new WeakMap<object, MinMaxValues>();

function getMinMaxForData(data: unknown[]): MinMaxValues {
  const cached = minMaxByData.get(data);
  if (cached) return cached;
  const valid = (data as number[]).filter((v) => v !== null && !isNaN(v));
  const result: MinMaxValues =
    valid.length === 0
      ? { minValue: Infinity, maxValue: -Infinity }
      : { minValue: Math.min(...valid), maxValue: Math.max(...valid) };
  minMaxByData.set(data, result);
  return result;
}

export function computeMinMaxValues(datasets: ChartData<'line'>['datasets']): MinMaxValues[] {
  return datasets.map((dataset) => getMinMaxForData(dataset.data));
}
export function hasMinMaxPoint(
  ctx: { dataIndex: number; datasetIndex: number; dataset: { data: unknown[] } },
  minMaxValues: MinMaxValues[] | null,
): boolean {
  if (minMaxValues === null) return false;
  const { minValue, maxValue } = getMinMaxForData(ctx.dataset.data);
  const value = ctx.dataset.data[ctx.dataIndex] as number;
  return value === minValue || value === maxValue;
}

export function hasMaxPoint(ctx: { dataIndex: number; dataset: { data: unknown[] } }): boolean {
  const value = ctx.dataset.data[ctx.dataIndex] as number;
  return value === getMinMaxForData(ctx.dataset.data).maxValue;
}

export function hasMinPoint(ctx: { dataIndex: number; dataset: { data: unknown[] } }): boolean {
  const value = ctx.dataset.data[ctx.dataIndex] as number;
  return value === getMinMaxForData(ctx.dataset.data).minValue;
}

/**
 * Used for smaller line charts
 * Limits the number of ticks to 2 or 3 (depending on the total number)
 * 2 ticks for even number of labels
 * 3 ticks for odd number of labels
 * @param axis
 * @param limitTicks
 * @returns
 */
export function limitTicksCallback(axis: Scale<CoreScaleOptions>, limitTicks?: boolean) {
  if (!axis.ticks.length) return;
  const { length } = axis.ticks;
  if (!limitTicks) {
    axis.ticks = axis.ticks.map((tick, index) => {
      if (index === 0 || index === length - 1) {
        return { ...tick, major: true };
      }
      return tick;
    });
    return;
  }

  if (length % 2 === 0) {
    axis.ticks = axis.ticks.filter((tick, index) => index === 0 || index === length - 1);
    return;
  }

  axis.ticks = axis.ticks.filter(
    (tick, index) => index === 0 || index === Math.floor(length / 2) || index === length - 1,
  );
}

export function buildTicks(color = '#696969', size = 15) {
  return {
    font: {
      family: window.fontFamily,
      size,
      weight: 500,
      lineHeight: '150%',
    },
    color,
  };
}

export function defaultYAxisTickCallback(value: number | string, locale: string) {
  if (typeof value === 'number') {
    return addNumberSeparator(value, locale);
  }

  return value;
}
